import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Plus, Minus } from 'lucide-react';

const StepEdgings = ({
  edgings,
  onEdgingChange,
  totals,
  onBack,
  onContinue
}) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-[#27292E] mb-2">
        Edgings
      </h2>
      <p className="text-gray-600 mb-6">
        Add any edging requirements. Enter lengths in metres (sleepers are per unit).
      </p>

      <div className="space-y-4">
        {edgings.map((item) => (
          <div 
            key={item.id}
            className={`border-2 rounded-xl p-4 transition-colors ${
              item.quantity > 0 
                ? 'border-[#300122] bg-[#300122]/5' 
                : 'border-gray-200'
            }`}
            data-testid={`edging-${item.id}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex-1">
                <h3 className="font-bold text-[#27292E]">{item.name}</h3>
                <p className="text-sm text-gray-500">
                  £{item.pricePerUnit} per {item.unit === 'm' ? 'metre' : item.unit}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onEdgingChange(item.id, Math.max(0, item.quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center bg-white border-2 border-[#300122] text-[#300122] rounded-lg hover:bg-[#300122]/10"
                    data-testid={`${item.id}-decrease`}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={item.quantity || ''}
                    onChange={(e) => onEdgingChange(item.id, e.target.value)}
                    placeholder="0"
                    className="w-20 h-10 text-center border-2 rounded-lg"
                    data-testid={`${item.id}-input`}
                  />
                  <button
                    onClick={() => onEdgingChange(item.id, item.quantity + 1)}
                    className="w-10 h-10 flex items-center justify-center bg-[#300122] text-white rounded-lg hover:bg-[#4a0636]"
                    data-testid={`${item.id}-increase`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-sm text-gray-500 w-16">
                  {item.unit === 'm' ? 'metres' : 'sleepers'}
                </span>
              </div>
            </div>
            {item.quantity > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                <span className="text-gray-600 text-sm">
                  {item.quantity} {item.unit === 'm' ? 'm' : 'sleeper(s)'} × £{item.pricePerUnit}
                </span>
                <span className="font-bold text-[#300122] text-lg">
                  £{(item.quantity * item.pricePerUnit).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Running Total */}
      <div className="mt-6 p-4 bg-[#300122] text-white rounded-xl">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-white/80">Surfacing:</span>
            <span>£{totals.surfacingTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
          </div>
          {totals.additionalTotal > 0 && (
            <div className="flex justify-between">
              <span className="text-white/80">Additional Costs:</span>
              <span>£{totals.additionalTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          {totals.edgingsTotal > 0 && (
            <div className="flex justify-between">
              <span className="text-white/80">Edgings:</span>
              <span>£{totals.edgingsTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-white/20">
            <span className="font-bold">Total:</span>
            <span className="text-2xl font-bold">
              £{totals.grandTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-4 mt-8">
        <Button
          onClick={onBack}
          variant="outline"
          className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#300122] text-[#300122] hover:bg-[#300122]/5"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
        <Button
          onClick={onContinue}
          className="flex-1 bg-[#300122] hover:bg-[#4a0636] text-white h-14 text-lg font-bold rounded-xl"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default StepEdgings;
